import { useEffect, useState } from 'react';
import { Navigate, useParams, Link } from 'react-router-dom';
import { tryUnprotectParam, screenService } from '@kss/api';
import { setTable } from '@kss/ui';

/**
 * QR landing route (`/t/:code`). Captures the table token from the scanned QR — decrypting it
 * if it's an `enc.…` token — VERIFIES the table exists and is enabled, then persists it and
 * redirects straight to the table's menu (ready to order). A disabled / unknown table is
 * refused with a clear message rather than silently opening a menu nobody can order from.
 *
 * A token that will not decrypt is refused HERE. It used to fall through as its own plaintext
 * (`enc.…`), which is not a table code: `setTable` took it, and from then on the customer's
 * every order, bill request and guest sign-in was rejected by the API.
 */
export default function TableEntry() {
  const { code = '' } = useParams();
  const [state, setState] = useState<'checking' | 'ok' | 'unavailable'>('checking');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const decoded = await tryUnprotectParam(code);
        const table = (decoded ?? '').toLowerCase();
        if (!table) {
          if (alive) setState('unavailable');
          return;
        }
        const menu = await screenService.getQrMenu(table);
        if (!alive) return;
        // setTable refuses anything that isn't a table code, so an unreadable token can't be
        // persisted even if the menu call happened to succeed.
        if (menu.tableAvailable && setTable(table)) {
          setState('ok');
        } else {
          setState('unavailable');
        }
      } catch {
        if (alive) setState('unavailable');
      }
    })();
    return () => {
      alive = false;
    };
  }, [code]);

  if (state === 'ok') return <Navigate to='/order' replace />;

  if (state === 'unavailable') {
    return (
      <div className='mx-auto flex min-h-[70vh] max-w-[360px] flex-col items-center justify-center gap-3 px-6 text-center'>
        <div className='flex h-14 w-14 items-center justify-center rounded-2xl bg-danger-light text-2xl'>🍽️</div>
        <div className='font-display text-lg font-bold'>Table not available</div>
        <p className='text-[13px] text-content-secondary'>
          This table isn’t taking orders right now. Please ask our staff to seat you, or scan a different table’s QR.
        </p>
        <Link to='/' className='mt-2 rounded-xl bg-primary px-5 py-2.5 font-display text-sm font-bold text-primary-foreground'>
          Browse the menu
        </Link>
      </div>
    );
  }

  return <div className='py-24 text-center text-sm text-content-muted'>Opening your table…</div>;
}
